/**
 * Weight / goal-date calculations. Kg-only — imperial display conversions
 * live in @render/utils/unitConversion and are applied at the render layer.
 *
 * All formulas here were carried over verbatim from the legacy flow; the
 * product-team comments on the two main functions are preserved so future
 * maintainers know which parts are policy vs. implementation detail.
 */

export const DEFAULT_GOAL_PACE_KG = 1.5
export const GOAL_PACE_KG_VALUES = [0.5, 1, 2] as const
export const MIN_SUGGESTED_WEIGHT_KG = 30
export const SUGGESTED_BMI = 21.1
export const MIN_HEALTHY_BMI = 18.5
export const MAX_HEALTHY_BMI = 24.9
export const FIRST_WEEK_MAX_LOSS_KG = 5

/**
 * Weight-gain pace model, applied on every brand whenever the target weight is
 * above the current weight: +3 kg across the first month, then +1 kg every two
 * weeks. The first month is spread evenly over its 4 weeks, which is what makes
 * the two-week projection 1.5 kg.
 */
export const GAIN_FIRST_MONTH_KG = 3
export const GAIN_FIRST_MONTH_WEEKS = 4
export const GAIN_FIRST_MONTH_PACE_KG = GAIN_FIRST_MONTH_KG / GAIN_FIRST_MONTH_WEEKS
export const GAIN_PACE_KG_PER_WEEK = 0.5
export const GAIN_IN_2_WEEKS_KG = GAIN_FIRST_MONTH_PACE_KG * 2
export const FIRST_WEEK_GAIN_KG = GAIN_FIRST_MONTH_PACE_KG

/**
 * Upper bound on any computed goal-day count (~100 years).
 *
 * Absurd weight input — a pasted value, a query-param-seeded variable, a unit
 * mix-up (grams for kg) — produces a day count far past the JS Date range
 * (±100,000,000 days from the epoch). `new Date(...).setDate(getDate() + days)`
 * then yields an Invalid Date, and the caller's `toISOString()` throws
 * "RangeError: Invalid time value" (the Bugsnag errors on the goal-date write).
 * A finite pace can also overflow the arithmetic to Infinity outright.
 *
 * Clamping here keeps every derived Date valid at the source. The bound is far
 * beyond any real goal, so no plausible input is affected.
 */
export const MAX_GOAL_DAYS = 36500

/** Normalize a raw day count to a whole, positive, Date-safe number of days. */
function clampGoalDays(days: number): number {
  if (!Number.isFinite(days)) return MAX_GOAL_DAYS
  return Math.min(MAX_GOAL_DAYS, Math.max(1, Math.round(Math.abs(days))))
}

/** `now` + `days`, on the local calendar. `days` must already be clamped. */
function addDays(now: Date, days: number): Date {
  const out = new Date(now.getTime())
  out.setDate(out.getDate() + days)

  return out
}

export type GoalPaceKg = (typeof GOAL_PACE_KG_VALUES)[number]

export interface SuggestedWeightInput {
  heightCm: number
}

export interface DaysTillGoalInput {
  weightKg: number
  targetWeightKg: number
  goalPaceKg?: number
}

/**
 * Suggested target weight derived from BMI 21.1 ("top of the healthy range"),
 * clamped to a 30 kg minimum so unrealistically low values never surface.
 */
export function getSuggestedWeightKg({ heightCm }: SuggestedWeightInput): number {
  const h = Number(heightCm)
  if (!Number.isFinite(h) || h <= 0) return MIN_SUGGESTED_WEIGHT_KG
  const raw = Math.round((h ** 2 * SUGGESTED_BMI) / 10000)
  return raw < MIN_SUGGESTED_WEIGHT_KG ? MIN_SUGGESTED_WEIGHT_KG : raw
}

export interface SuggestedWeightRangeInput {
  heightCm: number
  weightKg?: number
}

/** Lower bound of the healthy weight range — BMI 18.5 from heightCm. */
export function getMinSuggestedWeightKg({ heightCm }: SuggestedWeightInput): number | null {
  const height = Number(heightCm)
  if (!Number.isFinite(height) || height <= 0) return null

  // Ceil so the resulting integer kg never rounds back below 18.5 BMI
  // (e.g. 160cm → 47.36 kg; rounded to 47 gives BMI 18.4 = Underweight).
  return Math.ceil(MIN_HEALTHY_BMI * (height / 100) ** 2)
}

/**
 * Upper bound of the healthy weight range. When the user is already inside
 * the healthy BMI band, cap at `weightKg - 1` so they don't get suggested
 * targets above their current weight; otherwise use the BMI-24.9 ceiling.
 */
export function getMaxSuggestedWeightKg({
  heightCm,
  weightKg,
}: SuggestedWeightRangeInput): number | null {
  const height = Number(heightCm)
  if (!Number.isFinite(height) || height <= 0) return null
  const bmi = getBmi({ weightKg: Number(weightKg), heightCm: height })
  if (bmi !== null && bmi > MIN_HEALTHY_BMI && bmi < MAX_HEALTHY_BMI) {
    return Number(weightKg) - 1
  }
  // Floor so the resulting integer kg never rounds up above 24.9 BMI
  // (e.g. 160cm → 63.74 kg; rounded to 64 gives BMI 25.0 = Overweight).
  return Math.floor(MAX_HEALTHY_BMI * (height / 100) ** 2)
}

export interface BmiInput {
  weightKg: number
  heightCm: number
}

/** BMI = weight(kg) / height(m)^2. Returns null when inputs are invalid. */
export function getBmi({ weightKg, heightCm }: BmiInput): number | null {
  const weight = Number(weightKg)
  const height = Number(heightCm)
  if (!Number.isFinite(weight) || weight <= 0) return null
  if (!Number.isFinite(height) || height <= 0) return null
  const bmi = weight / (height / 100.0) ** 2
  return parseFloat(bmi.toFixed(1))
}

/**
 * Weeks needed to gain `gainKg`, per the gain pace model: the first
 * GAIN_FIRST_MONTH_KG spread evenly over GAIN_FIRST_MONTH_WEEKS, everything
 * above it at GAIN_PACE_KG_PER_WEEK.
 */
export function getWeeksTillGainGoal(gainKg: number): number {
  const gain = Number(gainKg)
  if (!Number.isFinite(gain) || gain <= 0) return 0
  if (gain <= GAIN_FIRST_MONTH_KG) return gain / GAIN_FIRST_MONTH_PACE_KG
  return GAIN_FIRST_MONTH_WEEKS + (gain - GAIN_FIRST_MONTH_KG) / GAIN_PACE_KG_PER_WEEK
}

/** Inverse of getWeeksTillGainGoal — kg gained by a (fractional) week count. */
export function getGainedByWeeksKg(weeks: number): number {
  const w = Number(weeks)
  if (!Number.isFinite(w) || w <= 0) return 0
  if (w <= GAIN_FIRST_MONTH_WEEKS) return w * GAIN_FIRST_MONTH_PACE_KG
  return GAIN_FIRST_MONTH_KG + (w - GAIN_FIRST_MONTH_WEEKS) * GAIN_PACE_KG_PER_WEEK
}

/**
 * Goal data calculation provided by the product team:
 * ((weightKg - targetWeightKg - 5kg or 1/3 of current weight for the 1st week
 *   (whichever is smaller)) / xkg/week + 1) * 7 = days to reach goal
 * Example:
 * ((90 - 78 - 5 [because 90/3 > 5kg]) / 2 + 1) * 7 = 35 days
 *
 * When the target is above the current weight the gain pace model replaces it
 * entirely — goalPaceKg is a loss-only setting and is ignored there.
 */
export function getDaysTillGoalDate({
  weightKg,
  targetWeightKg,
  goalPaceKg,
}: DaysTillGoalInput): number {
  const w = Number(weightKg)
  const t = Number(targetWeightKg)
  if (!Number.isFinite(w) || !Number.isFinite(t)) return 0

  const weightDiff = Math.abs(w - t)
  if (weightDiff === 0) return 1

  if (t > w) return clampGoalDays(getWeeksTillGainGoal(weightDiff) * 7)

  const pace =
    typeof goalPaceKg === 'number' && Number.isFinite(goalPaceKg) && goalPaceKg > 0
      ? goalPaceKg
      : DEFAULT_GOAL_PACE_KG
  const initialLoss = Math.min(5, weightDiff / 3)
  const days = ((weightDiff - initialLoss) / pace + 1) * 7
  return clampGoalDays(days)
}

// Used for AppLovin pipes
export function getDaysTillGoalDate1kg({
  weightKg,
  targetWeightKg,
  goalPaceKg,
}: DaysTillGoalInput): number {
  const w = Number(weightKg)
  const t = Number(targetWeightKg)
  if (!Number.isFinite(w) || !Number.isFinite(t)) return 0

  const weightDiff = Math.abs(w - t)
  if (weightDiff === 0) return 1

  if (t > w) return clampGoalDays(getWeeksTillGainGoal(weightDiff) * 7)

  const pace =
    typeof goalPaceKg === 'number' && Number.isFinite(goalPaceKg) && goalPaceKg > 0
      ? goalPaceKg
      : DEFAULT_GOAL_PACE_KG
  const initialLoss = pace
  const days = ((weightDiff - initialLoss) / pace + 1) * 7
  return clampGoalDays(days)
}

/**
 * "Faster progress" goal date — `getGoalDate` shaved by a static day count:
 * 2 days when daysTillGoal < 30, otherwise 5. No configurable override —
 * kept deliberately static. Pass `now` to make unit-testing deterministic.
 */
export function getFasterProgressDate(input: DaysTillGoalInput, now: Date = new Date()): Date {
  const daysTillGoal = getDaysTillGoalDate(input)
  const reduction = daysTillGoal < 30 ? 2 : 5

  return addDays(now, Math.max(1, daysTillGoal - reduction))
}

/** Today + getDaysTillGoalDate(input). Pass `now` to make unit-testing deterministic. */
export function getGoalDate(input: DaysTillGoalInput, now: Date = new Date()): Date {
  return addDays(now, getDaysTillGoalDate(input))
}

export function getGoalDate1kg(input: DaysTillGoalInput, now: Date = new Date()): Date {
  return addDays(now, getDaysTillGoalDate1kg(input))
}

/** Whole months remaining, rounded up. ≥1 unless inputs are invalid (then 0). */
export function getMonthsTillGoalDate(input: DaysTillGoalInput): number {
  const days = getDaysTillGoalDate(input)
  if (days <= 0) return 0
  return Math.max(1, Math.ceil(days / 30))
}

export interface OneMonthWeightLossInput {
  weightKg: number
  targetWeightKg: number
  goalPaceKg?: number
}

/**
 * Projected kg of change in the first month:
 *   loss path: min(5, weightDiff/3)  // initial loss in week 1
 *            + goalPaceKg * 3        // sustained pace over weeks 2-4
 *   gain path: GAIN_FIRST_MONTH_KG, capped at the goal difference
 */
export function getOneMonthWeightLossKg({
  weightKg,
  targetWeightKg,
  goalPaceKg,
}: OneMonthWeightLossInput): number {
  const w = Number(weightKg)
  const t = Number(targetWeightKg)
  if (!Number.isFinite(w) || !Number.isFinite(t)) return 0

  const weightDiff = Math.abs(w - t)
  if (weightDiff === 0) return 0

  if (t > w) return Math.round(Math.min(weightDiff, GAIN_FIRST_MONTH_KG) * 10) / 10

  const pace =
    typeof goalPaceKg === 'number' && Number.isFinite(goalPaceKg) && goalPaceKg > 0
      ? goalPaceKg
      : DEFAULT_GOAL_PACE_KG
  const initialLoss = Math.min(5, weightDiff / 3)
  return Math.round((initialLoss + pace * 3) * 10) / 10
}

export interface WeightLostByWeeksInput {
  weightKg: number
  targetWeightKg: number
  weeks: number
  goalPaceKg?: number
}

/**
 * Projected kg lost by an arbitrary future date, expressed as a (fractional)
 * number of weeks away. Same pace model as getOneMonthWeightLossKg:
 *   firstWeekLoss (= min(5, weightDiff/3)) + goalPaceKg * (weeks - 1)
 * capped at the current→target difference (you can never lose more than the
 * goal) and floored at 0, rounded to a whole kg. Weeks below 1 yield just the
 * first-week loss (the product model treats the first week as an all-or-nothing
 * initial drop).
 *
 * On the gain path the gain pace model replaces both terms and the result keeps
 * one decimal — whole-kg rounding would report the 1.5 kg two-week projection
 * as 2 kg.
 */
export function getWeightLostByWeeksKg({
  weightKg,
  targetWeightKg,
  weeks,
  goalPaceKg,
}: WeightLostByWeeksInput): number {
  const weight = Number(weightKg)
  const targetWeight = Number(targetWeightKg)
  const weeksAway = Number(weeks)
  if (!Number.isFinite(weight) || !Number.isFinite(targetWeight) || !Number.isFinite(weeksAway))
    return 0

  const weightDiff = Math.abs(weight - targetWeight)
  if (weightDiff === 0 || weeksAway <= 0) return 0

  if (targetWeight > weight) {
    return Math.round(Math.min(weightDiff, getGainedByWeeksKg(weeksAway)) * 10) / 10
  }

  const pace =
    typeof goalPaceKg === 'number' && Number.isFinite(goalPaceKg) && goalPaceKg > 0
      ? goalPaceKg
      : DEFAULT_GOAL_PACE_KG
  const initialLoss = Math.min(FIRST_WEEK_MAX_LOSS_KG, weightDiff / 3)
  const projected = initialLoss + pace * Math.max(0, weeksAway - 1)
  return Math.round(Math.min(weightDiff, Math.max(0, projected)))
}

export interface WeightDiffInput {
  weightKg: number
  targetWeightKg: number
}

/** Absolute weight diff in kg — always non-negative regardless of loss/gain direction. */
export function getDesiredWeightDiffKg({ weightKg, targetWeightKg }: WeightDiffInput): number {
  const w = Number(weightKg)
  const t = Number(targetWeightKg)
  if (!Number.isFinite(w) || !Number.isFinite(t)) return 0
  return Math.round(Math.abs(w - t) * 10) / 10
}

/** True when target ≥ current — user is gaining (or maintaining). */
export function isWeightGain({ weightKg, targetWeightKg }: WeightDiffInput): boolean {
  const w = Number(weightKg)
  const t = Number(targetWeightKg)
  if (!Number.isFinite(w) || !Number.isFinite(t)) return false
  return t >= w
}

/**
 * First week's projected weight loss in kg. Product rule:
 *   min(FIRST_WEEK_MAX_LOSS_KG, weightDiff / 3)
 * Mirrors the `initialLoss` term in getDaysTillGoalDate. Ceiled to a whole
 * kg for display ("lose X kg in your first week"). In the gain scenario
 * `getFirstWeekDiffKg` is the right caller; this helper falls back to the
 * cap so the value stays bounded if used directly.
 */
export function getFirstWeekLossKg({ weightKg, targetWeightKg }: WeightDiffInput): number {
  const w = Number(weightKg)
  const t = Number(targetWeightKg)
  if (!Number.isFinite(w) || !Number.isFinite(t)) return 0
  const targetDiff = w - t
  if (targetDiff <= 0) return FIRST_WEEK_MAX_LOSS_KG
  return Math.ceil(Math.min(targetDiff / 3, FIRST_WEEK_MAX_LOSS_KG))
}

/**
 * First week's projected weight change in kg, signed by intent:
 *   loss path: getFirstWeekLossKg
 *   gain path: FIRST_WEEK_GAIN_KG (first month's 3 kg spread over 4 weeks)
 */
export function getFirstWeekDiffKg({ weightKg, targetWeightKg }: WeightDiffInput): number {
  if (isWeightGain({ weightKg, targetWeightKg })) return FIRST_WEEK_GAIN_KG
  return getFirstWeekLossKg({ weightKg, targetWeightKg })
}

/** Snap a user-entered pace to the nearest value in GOAL_PACE_KG_VALUES. */
export function clampGoalPaceKg(paceKg: number): GoalPaceKg {
  const n = Number(paceKg)
  if (!Number.isFinite(n)) return DEFAULT_GOAL_PACE_KG as GoalPaceKg
  let best: GoalPaceKg = GOAL_PACE_KG_VALUES[0]
  let bestDist = Math.abs(n - best)
  for (const v of GOAL_PACE_KG_VALUES) {
    const d = Math.abs(n - v)
    if (d < bestDist) {
      best = v
      bestDist = d
    }
  }
  return best
}

/**
 * Second-week flat reduction subtracted from the first-week projection —
 * product's fixed constant for the "Weight in 2 weeks" card, independent of
 * goalPaceKg.
 */
export const SECOND_WEEK_FLAT_LOSS_KG = 1.5

/**
 * Projected weight 2 weeks out, per the PO calculation:
 *   startingWeight - firstWeekLoss - SECOND_WEEK_FLAT_LOSS_KG
 * where firstWeekLoss = min(FIRST_WEEK_MAX_LOSS_KG, weightDiff / 3) — the same
 * uncapped term getFirstWeekLossKg ceils for display; kept uncapped here to
 * avoid compounding rounding error before the final round.
 *
 * On the gain path (targetWeightKg > weightKg) the projection is instead
 *   startingWeight + GAIN_IN_2_WEEKS_KG
 * capped at the target, so it never overshoots the goal. Null when the target
 * equals the current weight — there is nothing to project.
 */
export function getWeightIn2WeeksKg({ weightKg, targetWeightKg }: WeightDiffInput): number | null {
  const w = Number(weightKg)
  const t = Number(targetWeightKg)
  if (!Number.isFinite(w) || !Number.isFinite(t)) return null

  const weightDiff = w - t
  if (weightDiff === 0) return null
  if (weightDiff < 0) return Math.round(Math.min(t, w + GAIN_IN_2_WEEKS_KG) * 10) / 10

  const firstWeekLoss = Math.min(FIRST_WEEK_MAX_LOSS_KG, weightDiff / 3)
  return Math.round((w - firstWeekLoss - SECOND_WEEK_FLAT_LOSS_KG) * 10) / 10
}

/** Excess weight (kg) at or above which belly/legs get the larger 2-week reduction. */
export const BODY_REDUCTION_LARGE_EXCESS_KG = 10
export const BELLY_REDUCTION_LARGE_CM = -3.5
export const BELLY_REDUCTION_SMALL_CM = -2.5
export const LEGS_REDUCTION_LARGE_CM = -2
export const LEGS_REDUCTION_SMALL_CM = -1.5

export interface BellyLegsIn2WeeksResult {
  bellyIn2Weeks: number
  legsIn2Weeks: number
}

/**
 * Belly/legs projected 2-week change, per the PO calculation:
 *   excess = weightKg - targetWeightKg
 *   excess >= 10    -> belly -3.5cm, legs -2cm
 *   0 < excess < 10 -> belly -2.5cm, legs -1.5cm
 *
 * Null in the gain/maintain scenario (targetWeightKg >= weightKg) — the
 * product spec gives no numeric formula for that case ('tighter'/'firm' copy
 * instead); gate that branch with a visibility rule on targetWeightKg vs
 * weightKg rather than reading this variable.
 */
export function getBellyLegsIn2Weeks({
  weightKg,
  targetWeightKg,
}: WeightDiffInput): BellyLegsIn2WeeksResult | null {
  const w = Number(weightKg)
  const t = Number(targetWeightKg)
  if (!Number.isFinite(w) || !Number.isFinite(t)) return null

  if (t >= w) return null

  const excessKg = w - t
  const isLargeExcess = excessKg >= BODY_REDUCTION_LARGE_EXCESS_KG
  return {
    bellyIn2Weeks: isLargeExcess ? BELLY_REDUCTION_LARGE_CM : BELLY_REDUCTION_SMALL_CM,
    legsIn2Weeks: isLargeExcess ? LEGS_REDUCTION_LARGE_CM : LEGS_REDUCTION_SMALL_CM,
  }
}

/**
 * The two weights as a percentage, always the lighter over the heavier so the
 * result never exceeds 100 and stays safe to feed a progress bar or donut:
 *   loss path (90 → 78 kg): end weight as % of start   = 87%
 *   gain path (35 → 54 kg): start weight as % of end   = 65%
 *
 * The meaning therefore flips with direction — copy that reads the variable
 * needs a visibility rule on targetWeightKg vs weightKg if it names which
 * weight the percentage is *of*. Equal weights give 100.
 */
export function percentOfCurrentWeight({ weightKg, targetWeightKg }: WeightDiffInput): number {
  const weight = Number(weightKg)
  const targetWeight = Number(targetWeightKg)
  if (!Number.isFinite(weight) || weight <= 0) return 0
  if (!Number.isFinite(targetWeight) || targetWeight <= 0) return 0

  if (targetWeight > weight) return Math.round((100 * weight) / targetWeight)

  return Math.round((100 * targetWeight) / weight)
}
